import {
  Component,
  inject,
  signal,
  computed,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { NotificationService } from '../services/notification.service'; // NotificationService;
import { FleetService } from './services/fleet.service';
import {
  FleetClient,
  FleetStats,
  FleetFilter,
  FleetListRequest,
  FleetPlateInput,
} from './models/fleet.model';
import {
  AddFleetModalComponent,
  AddFleetFormData,
  EXPIRY_OPTIONS,
  FleetStatus,
} from './components/add-fleet-modal/add-fleet-modal.component';
import {
  PaginationBarComponent,
  PaginationData,
} from '../shared/components/pagination-bar/pagination-bar.component';
import {
  SuccessModalComponent,
  SuccessModalData,
} from '../shared/components/success-modal/success-modal.component';
import { PackageService } from '../services/package.service';
import { LocationService } from '../services/location.service';
import { Package } from '../shared/types';
import {
  InvoiceModalComponent,
  InvoiceModalData,
} from './components/invoice-modal/invoice-modal.component';
import {
  ServiceHistoryModalComponent,
  ServiceHistoryModalData,
} from './components/service-history-modal/service-history-modal.component';
import { LicensePlatesModalComponent } from './components/license-plates-modal/license-plates-modal.component';
import { ConfirmActionModalComponent } from '../shared/components/confirm-action-modal';
import {
  AddLicensePlatesModalComponent,
  LicensePlateImportResponse,
} from './components/add-license-plates-modal/add-license-plates-modal.component';
import {
  LoadingTableShimmerComponent,
  ShimmerColumn,
} from '../shared/components/loading-table-shimmer/loading-table-shimmer.component';
import { TitleBarComponent } from '../shared/components/title-bar/title-bar.component';
import { StatsCardComponent } from '../shared/components/stats-card/stats-card.component';
import { SearchbarComponent } from '../shared/components/searchbar/searchbar.component';
import { ActionButtonComponent } from '../shared/components/action-button/action-button.component';
import {
  FilterModalComponent,
  FilterCategory,
} from '../shared/components/filter-modal/filter-modal.component';

@Component({
  selector: 'app-fleet',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    AddFleetModalComponent,
    PaginationBarComponent,
    SuccessModalComponent,
    InvoiceModalComponent,
    ServiceHistoryModalComponent,
    LicensePlatesModalComponent,
    AddLicensePlatesModalComponent,
    ConfirmActionModalComponent,
    LoadingTableShimmerComponent,
    TitleBarComponent,
    StatsCardComponent,
    SearchbarComponent,
    ActionButtonComponent,
    FilterModalComponent,
  ],
  templateUrl: './fleet.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./fleet.component.scss'],
})
export class FleetComponent implements OnInit {
  private readonly fleetService = inject(FleetService);
  private readonly messageService = inject(NotificationService);
  private readonly packageService = inject(PackageService);
  private readonly locationService = inject(LocationService);

  // Signals
  readonly fleetClients = signal<FleetClient[]>([]);
  readonly fleetStats = signal<FleetStats>({
    totalFleets: 0,
    totalVehicles: 0,
    activeContracts: 0,
    monthlyRevenue: 0,
  });
  readonly searchTerm = signal<string>('');
  readonly currentFilter = signal<FleetFilter>({});
  readonly showFilterModal = signal<boolean>(false);

  // Filter properties for action button
  readonly hasActiveFilters = computed(() => {
    const filter = this.currentFilter();
    return Boolean(filter.statuses?.length || filter.contractDurations?.length);
  });

  readonly getActiveFilterCount = computed(() => {
    const filter = this.currentFilter();
    return (filter.statuses?.length || 0) + (filter.contractDurations?.length || 0);
  });

  // Filter categories for shared filter modal
  readonly filterCategories = signal<FilterCategory[]>([
    {
      id: 'contractDurations',
      name: 'Contract Duration',
      options: [
        { id: '6', label: '6 Months', value: '6', selected: false },
        { id: '12', label: '12 Months', value: '12', selected: false },
        { id: '24', label: '24 Months', value: '24', selected: false },
        { id: '36', label: '36 Months', value: '36', selected: false },
        { id: '60', label: '60 Months', value: '60', selected: false },
      ],
    },
    {
      id: 'statuses',
      name: 'Status',
      options: [
        { id: 'active', label: 'Active', value: 'active', selected: false },
        { id: 'not_started', label: 'Not Started', value: 'not_started', selected: false },
        { id: 'inactive', label: 'Inactive', value: 'inactive', selected: false },
      ],
    },
  ]);
  readonly showAddFleetModal = signal<boolean>(false);
  readonly showSuccessModal = signal<boolean>(false);
  readonly error = signal<string | null>(null);

  // Edit fleet modal data
  readonly showEditFleetModal = signal<boolean>(false);
  /** Edit whose Active/Inactive change is waiting on the confirm modal. */
  readonly pendingStatusChange = signal<AddFleetFormData | null>(null);
  /** Message for the activation confirm, e.g. "Following Fleet will be activated: Acme." */
  readonly statusChangeMessage = computed(() => {
    const pending = this.pendingStatusChange();
    if (!pending) return '';
    return pending.status === 'active'
      ? `Following Fleet will be activated: ${pending.client}.`
      : `Following Fleet will be deactivated: ${pending.client}. Redemptions and scheduled invoices will stop.`;
  });

  readonly editFleetData = signal<AddFleetFormData | null>(null);

  // Transition states for smooth modal flow
  readonly isTransitioning = signal<boolean>(false);

  // Search debouncing
  private searchSubject = new Subject<string>();

  // Success modal data
  readonly successModalData = signal<SuccessModalData>({
    title: 'New Fleet Created!',
    description:
      'A new fleet has been created and added to your dashboard. You can make changes later if needed.',
    buttonText: 'Okay',
  });

  // Invoice modal data
  readonly showInvoiceModal = signal<boolean>(false);
  readonly invoiceModalData = signal<InvoiceModalData | null>(null);

  // Service history modal data
  readonly showServiceHistoryModal = signal<boolean>(false);
  readonly serviceHistoryModalData = signal<ServiceHistoryModalData | null>(null);

  // License plates modal data
  readonly showLicensePlatesModal = signal<boolean>(false);
  readonly selectedFleetId = signal<string>('');
  readonly selectedFleetName = signal<string>('');
  readonly selectedFleetLocation = signal<string>('');

  // Loading state
  readonly loading = computed(() => this.fleetService.getLoading());

  // Shimmer loading configuration
  readonly shimmerColumns: ShimmerColumn[] = [
    { label: 'Business Name', width: 'md' },
    { label: 'Status', width: 'sm' },
    { label: 'Package', width: 'sm' },
    { label: 'Charged Price', width: 'sm' },
    { label: 'Location', width: 'sm' },
    { label: 'Billing Cycle', width: 'sm' },
    { label: 'Expiry', width: 'sm' },
    { label: 'Vehicles', width: 'sm' },
    { label: 'Actions', width: 'xs' },
  ];

  // Add license plates modal data
  readonly showAddLicensePlatesModal = signal<boolean>(false);

  // CSV preview modal data
  readonly csvPreviewData = signal<{
    fleetName: string;
    licensePlates: FleetPlateInput[];
  } | null>(null);

  // License plates modal mode
  readonly licensePlatesModalMode = signal<'preview' | 'view' | 'edit'>('view');

  // Package data
  readonly packages = signal<Package[]>([]);

  // Dropdown menu properties
  readonly activeFleetActionsDropdown = signal<string | null>(null);
  readonly selectedFleetForDropdown = signal<FleetClient | null>(null);

  // Pagination
  readonly currentPage = signal<number>(1);
  readonly itemsPerPage = signal<number>(10);
  readonly totalPages = signal<number>(1);
  readonly totalItems = signal<number>(0);

  // Computed values
  readonly filteredClients = computed(() => this.fleetClients());

  readonly totalClients = computed(() => this.filteredClients().length);
  // Fleet statistics from API
  readonly apiFleetStats = signal<any>(null);
  readonly fleetStatsWithIcons = computed(() =>
    this.fleetService.getFleetStatsWithIcons(this.apiFleetStats())
  );

  /**
   * Initialize fleet component: loads fleet data, statistics and packages, sets up search debouncing, and attaches a document click listener to close action dropdowns when clicking outside.
   * @example
   * gOnInit()
   * undefined
   * @returns {{void}} No return value; performs initialization and side-effect operations.
   **/
  ngOnInit(): void {
    this.locationService.clearShopLocation();
    this.loadFleetData();
    this.loadFleetStatistics();
    this.loadPackages();
    this.setupSearchDebouncing();

    // Close dropdown when clicking outside
    document.addEventListener('click', event => {
      const target = event.target as HTMLElement;
      const dropdown = document.querySelector('.actions-dropdown-menu');
      const actionButton = target.closest('.actions-button');

      // If click is not on dropdown or action button, close dropdown
      if (
        this.activeFleetActionsDropdown() &&
        dropdown &&
        !dropdown.contains(target) &&
        !actionButton
      ) {
        this.activeFleetActionsDropdown.set(null);
        this.selectedFleetForDropdown.set(null);
      }
    });
  }

  private setupSearchDebouncing(): void {
    this.searchSubject
      .pipe(
        debounceTime(300), // Wait 300ms after user stops typing
        distinctUntilChanged() // Only emit if the value has changed
      )
      .subscribe(searchTerm => {
        this.performSearch(searchTerm);
      });
  }

  /**
   * Load fleet statistics from the backend and update apiFleetStats; if the API call fails, log the error and set apiFleetStats to null as a fallback.
   * @example
   * loadFleetStatistics()
   * undefined
   * @returns {void} Triggers an asynchronous request to refresh fleet statistics and updates internal state; does not return a value.
   **/
  private loadFleetStatistics(): void {
    this.fleetService.getFleetStatistics().subscribe({
      next: stats => {
        this.apiFleetStats.set(stats);
      },
      error: error => {
        console.error('Error loading fleet statistics:', error);
        // Keep using fallback stats if API fails
        this.apiFleetStats.set(null);
      },
    });
  }

  /**
   * Load and set active car wash packages by fetching them from the package service.
   * @example
   * loadPackages()
   * undefined
   * @param {void} none - No parameters.
   * @returns {void} Returns nothing; sets this.packages with active packages or logs an error.
   **/
  private loadPackages(): void {
    this.packageService.getCarWashPackages().subscribe({
      next: response => {
        if (response.status && response.data) {
          // Filter to show only active packages (package_status === true)
          const activePackages = response.data.filter(
            (pkg: Package) => pkg.package_status === true
          );
          this.packages.set(activePackages);
        }
      },
      error: error => {
        console.error('Error loading packages:', error);
      },
    });
  }

  /**
   * Load fleet data based on current filters, pagination and search state; resets stale data and updates component observables.
   * @example
   * loadFleetData()
   * undefined
   * @returns {{void}} Triggers an asynchronous fleet load, updates fleetClients, fleetStats, totalItems and totalPages, and sets error state on failure.
   **/
  private loadFleetData(): void {
    this.error.set(null);

    // Clear existing data immediately to prevent showing stale data
    this.fleetClients.set([]);
    this.totalItems.set(0);
    this.totalPages.set(1);

    const filter = this.currentFilter();
    const request: FleetListRequest = {
      page: this.currentPage(),
      limit: this.itemsPerPage(),
      search: this.searchTerm() || undefined,
      status: filter.statuses && filter.statuses.length > 0 ? filter.statuses.join(',') : undefined,
      contract_duration_months:
        filter.contractDurations && filter.contractDurations.length > 0
          ? filter.contractDurations.join(',')
          : undefined,
    };

    this.fleetService.loadFleets(request).subscribe({
      next: response => {
        this.fleetClients.set(response.fleets);
        this.fleetStats.set(this.fleetService.getFleetStats());
        this.totalItems.set(response.pagination.total);
        this.totalPages.set(Math.ceil(response.pagination.total / response.pagination.limit));
      },
      error: error => {
        console.error('Error loading fleet data:', error);
        this.error.set(error.message || 'Failed to load fleet data');
      },
    });
  }

  onSearchChange(searchTerm: string): void {
    this.searchTerm.set(searchTerm);
    this.currentPage.set(1); // Reset to first page when searching
    this.loadFleetData(); // Reload data with current search term
  }

  private performSearch(searchTerm: string): void {
    this.currentFilter.update(filter => ({ ...filter, searchTerm }));
    this.currentPage.set(1); // Reset to first page when searching
    this.loadFleetData(); // Reload data with search term
  }

  onFilterChange(filter: FleetFilter): void {
    this.currentFilter.set(filter);
    this.showFilterModal.set(false);
    this.currentPage.set(1); // Reset to first page when filtering
    this.loadFleetData(); // Reload data with filters
  }

  onShowFilterModal(): void {
    this.showFilterModal.set(true);
    this.updateFilterCategoriesFromCurrentFilter();
  }

  onCloseFilterModal(): void {
    this.showFilterModal.set(false);
  }

  /**
   * Populate a FleetFilter from modal categories and invoke onFilterChange.
   * @example
   * nFilterModalApply([{ id: 'vehicleTypes', options: [{ value: 'car', selected: true }] }])
   * undefined
   * @param {{FilterCategory[]}} {{categories}} - Array of filter categories from the modal.
   * @returns {{void}} No return value; applies the constructed filter by calling onFilterChange.
   **/
  onFilterModalApply(categories: FilterCategory[]): void {
    // Convert shared filter modal data to FleetFilter format
    const statuses =
      categories
        .find(cat => cat.id === 'statuses')
        ?.options.filter(opt => opt.selected)
        .map(opt => opt.value) || [];

    const contractDurations =
      categories
        .find(cat => cat.id === 'contractDurations')
        ?.options.filter(opt => opt.selected)
        .map(opt => opt.value) || [];

    const filter: FleetFilter = {
      searchTerm: this.searchTerm(),
      statuses,
      contractDurations,
    };

    this.onFilterChange(filter);
  }

  /**
   * Update filterCategories so each option's selected flag reflects the currentFilter.
   * @example
   * this.updateFilterCategoriesFromCurrentFilter()
   * // Updates the component's filterCategories store; no value is returned.
   * @returns {void} No return value; updates internal state (filterCategories) based on currentFilter.
   */
  private updateFilterCategoriesFromCurrentFilter(): void {
    const currentFilter = this.currentFilter();

    this.filterCategories.update(categories =>
      categories.map(category => ({
        ...category,
        options: category.options.map(option => ({
          ...option,
          selected: this.isOptionSelected(category.id, option.value, currentFilter),
        })),
      }))
    );
  }

  /**
   * Determine whether a given option value is selected in the provided fleet filter for a specific category.
   * @example
   * isOptionSelected('vehicleTypes', 'car', { vehicleTypes: ['car'], durations: [], frequencies: [] })
   * true
   * @param {{string}} {{categoryId}} - Category identifier to check ('vehicleTypes' | 'durations' | 'frequencies').
   * @param {{string}} {{value}} - Option value to test for presence in the selected category.
   * @param {{FleetFilter}} {{filter}} - Filter object containing arrays for vehicleTypes, durations, and frequencies.
   * @returns {{boolean}} Return true if the value is present in the corresponding category array; otherwise false.
   **/
  private isOptionSelected(categoryId: string, value: string, filter: FleetFilter): boolean {
    switch (categoryId) {
      case 'statuses':
        return filter.statuses?.includes(value) || false;
      case 'contractDurations':
        return filter.contractDurations?.includes(value) || false;
      default:
        return false;
    }
  }

  onAddFleet(): void {
    this.showAddFleetModal.set(true);
  }

  onCloseAddFleetModal(): void {
    this.showAddFleetModal.set(false);
  }

  onCloseLicensePlatesModal(): void {
    this.showLicensePlatesModal.set(false);
    this.selectedFleetId.set('');
    this.selectedFleetName.set('');
    this.selectedFleetLocation.set('');
  }

  onCloseAddLicensePlatesModal(): void {
    this.showAddLicensePlatesModal.set(false);
    this.selectedFleetId.set('');
    this.selectedFleetName.set('');
    this.selectedFleetLocation.set('');
  }

  /**
   * Handle license plate import response, refresh fleet data and show a summary success modal.
   * @example
   * nLicensePlatesImported({ data: { insertedPlates: ['ABC123'], skipped: [], invalid: [] } })
   * undefined
   * @param {LicensePlateImportResponse} response - Import response containing arrays of inserted, skipped, and invalid plates.
   * @returns {void} No return value.
   **/
  onLicensePlatesImported(response: LicensePlateImportResponse): void {
    // Reload fleet data to update the license plate status
    this.loadFleetData();
    this.loadFleetStatistics();

    // Build success message with all counts
    const importedCount = response.data.insertedPlates.length;
    const skippedCount = response.data.skipped?.length || 0;
    const invalidCount = response.data.invalid?.length || 0;

    let description = `Successfully imported ${importedCount} license plate${importedCount !== 1 ? 's' : ''}.`;

    if (skippedCount > 0) {
      description += ` ${skippedCount} plate${skippedCount !== 1 ? 's' : ''} ${skippedCount === 1 ? 'was' : 'were'} skipped as ${skippedCount === 1 ? 'it already exists' : 'they already exist'}.`;
    }

    if (invalidCount > 0) {
      description += ` ${invalidCount} plate${invalidCount !== 1 ? 's' : ''} ${invalidCount === 1 ? 'was' : 'were'} invalid and ${invalidCount === 1 ? 'was' : 'were'} not imported.`;
    }

    // Show success message
    this.successModalData.set({
      title: 'License Plates Imported!',
      description: description,
      buttonText: 'Okay',
    });
    this.showSuccessModal.set(true);
  }

  onViewFile(data: { fleetName: string; licensePlates: FleetPlateInput[] }): void {
    this.csvPreviewData.set(data);
    this.licensePlatesModalMode.set('preview'); // Set to preview mode for CSV data
    this.showLicensePlatesModal.set(true);
  }

  onCloseCsvPreview(): void {
    this.csvPreviewData.set(null);
    this.licensePlatesModalMode.set('view'); // Reset to view mode
    this.showLicensePlatesModal.set(false);
    this.selectedFleetLocation.set('');
  }

  onImportLicensePlatesFromModal(): void {
    // Close the license plates modal and open the add license plates modal
    this.showLicensePlatesModal.set(false);
    this.showAddLicensePlatesModal.set(true);
  }

  onEditLicensePlate(event: { id: string; newValue: string }): void {
    // A license plate value was updated; refresh stats to ensure vehicle counts stay in sync
    this.loadFleetData();
    this.loadFleetStatistics();
  }

  onDeleteLicensePlate(plateId: string): void {
    // License plate deleted successfully - no need for success modal
    // The license plates modal already handles the UI feedback and updates the list
    // Reload fleet data to update the license plate count
    this.loadFleetData();
    this.loadFleetStatistics();
  }

  /**
   * Build the payload object for creating or updating a fleet from form data and an optional package ID.
   * @param {{AddFleetFormData}} {{formData}} - AddFleetFormData object containing fleet form values.
   * @param {{string|number|undefined}} {{packageId}} - Optional package identifier to associate with the fleet.
   * @returns {{object}} Payload object formatted for the fleet API.
   **/
  /** Reverse-maps the display string (e.g. "12 months") from `FleetClient.duration` back to the Duration dropdown's month value. */
  private parseExpiryMonths(duration: string): number {
    const match = (duration || '').match(/(\d+)/);
    const months = match ? parseInt(match[1], 10) : 12;
    return EXPIRY_OPTIONS.some(option => option.value === months) ? months : 12;
  }

  /** "Mar 1, 2027" for a YYYY-MM-DD calendar date (parsed at local noon, no UTC day shift). */
  private formatCalendarDate(iso: string): string {
    const [y, m, d] = String(iso || '')
      .split('T')[0]
      .split('-')
      .map(Number);
    if (!y || !m || !d) return String(iso || '');
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(new Date(y, m - 1, d, 12));
  }

  /** Duration cell, e.g. "1 year"; informational only. */
  formatDuration(client: FleetClient): string {
    const months = parseInt(client.duration, 10);
    return EXPIRY_OPTIONS.find(option => option.value === months)?.label || client.duration || '-';
  }

  /** Charged Price cell, e.g. "$25.00 / wash". */
  formatChargedPrice(client: FleetClient): string {
    if (client.pricePerVehicle === null || client.pricePerVehicle === undefined) {
      return '-';
    }
    const unit = client.pricingType === 'per_wash' ? 'wash' : 'vehicle';
    return `$${client.pricePerVehicle.toFixed(2)} / ${unit}`;
  }

  private parseNotificationRecipients(raw: string): string[] {
    return (raw || '')
      .split(/[,\s]+/)
      .map(email => email.trim())
      .filter(email => email.length > 0);
  }

  private buildFleetPayload(
    formData: AddFleetFormData,
    packageId: string | number | undefined,
    isCreate = false
  ): object {
    const locationIds = (formData.locationIds || []).filter(Boolean);
    const payload: Record<string, unknown> = {
      business_name: formData.client,
      status: formData.status,
      contact_number: formData.contactNumber,
      email: formData.email,
      price_per_vehicle: formData.pricePerVehicle || 0,
      pricing_type: formData.pricingType || 'per_vehicle',
      vehicle_count_declared: formData.vehicleCount || 0,
      avg_washes_month: formData.avgWashesPerMonth ?? null,
      contract_duration_months: formData.expiryMonths,
      billing_cycle_type: formData.billingCycleType,
      payment_term_days: formData.paymentTermDays || 30,
      location_id: locationIds[0],
      location_ids: locationIds,
      package_id: packageId,
      notification_recipients: this.parseNotificationRecipients(formData.notificationRecipients),
    };
    if (isCreate) {
      const startDate = formData.startDate || new Date().toISOString().split('T')[0];
      payload.start_date = startDate;
      // Create defaults anchor to start; keep them aligned when the UI edits start date.
      payload.billing_cycle_anchor = startDate;
    } else if (formData.pricingType === 'per_wash' && formData.priceEffective) {
      // Start date (and the anchor tied to it) is fixed after create, so edits don't send it.
      payload.price_effective = formData.priceEffective;
    }
    return payload;
  }

  /**
   * Create a new fleet using the provided form data, manage UI transitions, and display success or error feedback.
   * @example
   * nSaveFleet({ package: 'Standard', vehicle_name: 'Fleet A', /* other AddFleetFormData fields */
  onSaveFleet(formData: AddFleetFormData): void {
    // Find the selected package to get its ID
    const selectedPackage = this.packages().find(
      (pkg: Package) => pkg.package_name === formData.package
    );
    if (!selectedPackage) {
      console.error('Selected package not found');
      return;
    }

    // Start transition process
    this.isTransitioning.set(true);
    const fleetData = this.buildFleetPayload(formData, selectedPackage.id, true);

    this.fleetService.createFleet(fleetData).subscribe({
      next: (response: any) => {
        if (response.status) {
          // Close add fleet modal with animation
          this.showAddFleetModal.set(false);

          // Reload fleet data to include the newly created fleet
          this.loadFleetData();

          // Reload fleet statistics to update stats
          this.loadFleetStatistics();

          // Wait for modal close animation, then show success modal
          setTimeout(() => {
            // Set the success modal data for fleet creation
            this.successModalData.set({
              title: 'New Fleet Created!',
              description:
                'A new fleet has been created and added to your dashboard. You can make changes later if needed.',
              buttonText: 'Okay',
            });
            this.showSuccessModal.set(true);
            this.isTransitioning.set(false);
          }, 300); // Match the modal close animation duration
        } else {
          // Handle API error response (e.g., duplicate fleet)
          console.error('Failed to create fleet:', response);
          this.isTransitioning.set(false);

          // Show error toast if it's a duplicate fleet error
          if (response.status_code === 400 && response.message === 'Fleet already exists') {
            this.messageService.add({
              severity: 'error',
              summary: 'Fleet Already Exists',
              detail:
                'A fleet with this vehicle name already exists. Please choose a different name.',
              life: 5000,
            });
          } else {
            // Show generic error toast
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: response.message || 'Failed to create fleet. Please try again.',
              life: 5000,
            });
          }
        }
      },
      error: (error: any) => {
        console.error('Error creating fleet:', error);
        this.isTransitioning.set(false);

        // Check if it's an API error response with the duplicate fleet message
        if (
          error.error &&
          error.error.status_code === 400 &&
          error.error.message === 'Fleet already exists'
        ) {
          this.messageService.add({
            severity: 'error',
            summary: 'Fleet Already Exists',
            detail:
              'A fleet with this vehicle name already exists. Please choose a different name.',
            life: 5000,
          });
        } else if (error.error && error.error.message) {
          // Show API error message if available
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error.error.message,
            life: 5000,
          });
        } else {
          // Show generic error toast for network or other errors
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to create fleet. Please check your connection and try again.',
            life: 5000,
          });
        }
      },
    });
  }

  onSuccessModalClose(): void {
    this.showSuccessModal.set(false);
    // Reset success modal data to default fleet creation message
    this.successModalData.set({
      title: 'New Fleet Created!',
      description:
        'A new fleet has been created and added to your dashboard. You can make changes later if needed.',
      buttonText: 'Okay',
    });
  }

  /**
   * Open the license plate management UI for a given fleet client by selecting the fleet and showing either the add or view modal.
   * @example
   * nManageLicensePlates('client-123')
   * undefined
   * @param {{string}} {{clientId}} - The id of the fleet client to manage license plates for.
   * @returns {{void}} Does not return a value; updates component state and opens the appropriate license plates modal.
   **/
  onManageLicensePlates(clientId: string): void {
    const client = this.fleetClients().find(c => c.id === clientId);
    if (client) {
      this.selectedFleetId.set(clientId);
      this.selectedFleetName.set(client.clientName);
      this.selectedFleetLocation.set(client.branch || '');

      if (client.isLicensePlateAdded) {
        // Show existing license plates in view mode
        this.licensePlatesModalMode.set('view');
        this.showLicensePlatesModal.set(true);
      } else {
        this.showAddLicensePlatesModal.set(true);
      }
    }
  }

  /**
   * Prepare and open the invoice modal using data derived from the given fleet client.
   * @example
   * nInvoiceClick({ id: 'fleet-123', clientName: 'ACME Corp', vehicles: '12', pricePerVehicle: 100 })
   * undefined
   * @param {{FleetClient}} {{client}} - Fleet client object used to populate invoice modal fields.
   * @returns {{void}} No return value; displays the invoice modal and sets its data.
   **/
  onInvoiceClick(client: FleetClient): void {
    const invoiceData: InvoiceModalData = {
      fleetId: client.id,
      fleetName: client.clientName,
      totalVehicles: client.vehicleCount || 0,
      pricePerVehicle: client.pricePerVehicle,
      subtotal: 0,
      tax: 0,
      grandTotal: 0,
      billingCycleType: client.billingCycleTypeRaw || 'monthly',
      billingCycleAnchor: client.billingCycleAnchor || client.startDate || '',
    };

    this.showInvoiceModal.set(true);
    this.invoiceModalData.set(invoiceData);
  }

  /** Called after Download pdf succeeds — close the modal. */
  onInvoiceGenerate(): void {
    this.showInvoiceModal.set(false);
    this.invoiceModalData.set(null);
  }

  onInvoiceClose(): void {
    this.showInvoiceModal.set(false);
    this.invoiceModalData.set(null);
  }

  /**
   * Open the History modal for a fleet; the modal loads service/edit history itself.
   */
  onViewServiceHistory(client: FleetClient): void {
    this.showServiceHistoryModal.set(true);
    this.serviceHistoryModalData.set({
      fleetId: client.id,
      fleetName: client.clientName,
    });
  }

  onServiceHistoryClose(): void {
    this.showServiceHistoryModal.set(false);
    this.serviceHistoryModalData.set(null);
  }

  /** Status column label (active / not_started / inactive); duration never expires a fleet. */
  formatContractStatus(status: string | undefined): string {
    switch ((status || 'active').toLowerCase()) {
      case 'not_started':
        return 'Not Started';
      case 'inactive':
        return 'Inactive';
      case 'active':
      default:
        return 'Active';
    }
  }

  trackByClientId(index: number, client: FleetClient): string {
    return client.id;
  }

  // Pagination methods
  onPageChange(page: number): void {
    this.currentPage.set(page);
    this.loadFleetData();
  }

  onItemsPerPageChange(itemsPerPage: number): void {
    this.itemsPerPage.set(itemsPerPage);
    this.currentPage.set(1);
    this.loadFleetData();
  }

  get paginationData(): PaginationData {
    return {
      currentPage: this.currentPage(),
      totalPages: this.totalPages(),
      totalItems: this.totalItems(),
      itemsPerPage: this.itemsPerPage(),
    };
  }

  // Dropdown menu methods
  /**
   * Toggles the fleet actions dropdown for a given fleet id; stops event propagation, updates the active dropdown and selected fleet, and schedules dropdown positioning.
   * @example
   * oggleFleetActionsDropdown(event, 'fleet-123')
   * undefined
   * @param {{Event}} {{event}} - The DOM event that triggered the toggle (propagation will be stopped).
   * @param {{string}} {{fleetId}} - The id of the fleet whose actions dropdown should be toggled.
   * @returns {{void}} No return value; updates component state and positions the dropdown asynchronously.
   **/
  toggleFleetActionsDropdown(event: Event, fleetId: string): void {
    event.stopPropagation();

    if (this.activeFleetActionsDropdown() === fleetId) {
      this.activeFleetActionsDropdown.set(null);
      this.selectedFleetForDropdown.set(null);
    } else {
      this.activeFleetActionsDropdown.set(fleetId);
      const selectedFleet = this.fleetClients().find(fleet => fleet.id === fleetId);
      this.selectedFleetForDropdown.set(selectedFleet || null);

      // Position the dropdown correctly
      setTimeout(() => {
        this.positionDropdown(event);
      }, 0);
    }
  }

  /**
   * Positions the ".actions-dropdown-menu" element near the event target (button), ensuring it remains within the viewport.
   * @example
   * positionDropdown(event)
   * undefined
   * @param {{Event}} {{event}} - The DOM event triggered by the actions button; its target is used to calculate dropdown position.
   * @returns {{void}} Void return; the function applies styles directly to the dropdown element.
   **/
  private positionDropdown(event: Event): void {
    const button = event.target as HTMLElement;
    const dropdown = document.querySelector('.actions-dropdown-menu') as HTMLElement;

    if (button && dropdown) {
      const buttonRect = button.getBoundingClientRect();
      const dropdownWidth = 140;
      const dropdownHeight = 88; // Approximate height for 2 items

      // Calculate position
      let left = buttonRect.right - dropdownWidth;
      let top = buttonRect.bottom + 8;

      // Ensure dropdown stays within viewport
      if (left < 8) {
        left = 8;
      }
      if (left + dropdownWidth > window.innerWidth - 8) {
        left = window.innerWidth - dropdownWidth - 8;
      }
      if (top + dropdownHeight > window.innerHeight - 8) {
        top = buttonRect.top - dropdownHeight - 8;
      }

      // Apply position
      dropdown.style.left = `${left}px`;
      dropdown.style.top = `${top}px`;
    }
  }

  /**
   * Populate and open the edit fleet modal with data for the given fleet ID.
   * Prefers GET /fleet/:id so contact/email/recipients (omitted from list) are filled.
   * @example
   * nEditFleet('fleet-123')
   * void
   * @param {{string}} {{fleetId}} - The ID of the fleet to edit.
   * @returns {{void}} Void (no return value).
   **/
  onEditFleet(fleetId: string): void {
    this.activeFleetActionsDropdown.set(null);
    this.selectedFleetForDropdown.set(null);

    this.fleetService.getFleetById(fleetId).subscribe({
      next: detail => {
        const packageId = detail.package_id || '';
        const selectedPackage = this.packages().find(
          (pkg: Package) => String(pkg.id) === String(packageId)
        );
        const listRow = this.fleetClients().find(f => f.id === fleetId);
        const rawCycle = (
          detail.billing_cycle_type ||
          listRow?.billingCycleTypeRaw ||
          'monthly'
        ).toLowerCase();
        // UI no longer offers custom; coerce legacy custom fleets to monthly for the form.
        const billingCycleType: AddFleetFormData['billingCycleType'] =
          rawCycle === 'weekly' || rawCycle === 'biweekly' || rawCycle === 'monthly'
            ? rawCycle
            : 'monthly';

        const toDateOnly = (value: unknown, fallback = ''): string => {
          if (value == null || value === '') return fallback;
          return String(value).split('T')[0];
        };

        const startDate = toDateOnly(detail.start_date, listRow?.startDate || '');
        const recipients = Array.isArray(detail.notification_recipients)
          ? detail.notification_recipients
          : [];
        const durationLabel =
          detail.contract_duration_months != null
            ? `${detail.contract_duration_months} months`
            : listRow?.duration || '12 months';

        let locationIds: string[] = [];
        if (Array.isArray(detail.location_ids) && detail.location_ids.length > 0) {
          locationIds = detail.location_ids.map(id => String(id));
        } else if (detail.location_id) {
          locationIds = [detail.location_id];
        } else if (listRow?.locationId) {
          locationIds = [listRow.locationId];
        }

        const status: FleetStatus = detail.status === 'inactive' ? 'inactive' : 'active';
        const formData: AddFleetFormData = {
          fleetId: detail.id,
          status,
          originalStatus: status,
          client: detail.business_name || listRow?.clientName || '',
          contactNumber: detail.contact_number || '',
          email: detail.email || '',
          // Price in force today; price_per_vehicle may already hold a scheduled price.
          pricePerVehicle:
            detail.current_price ?? detail.price_per_vehicle ?? listRow?.pricePerVehicle ?? 0,
          pricingType: detail.pricing_type === 'per_wash' ? 'per_wash' : 'per_vehicle',
          priceEffective: null,
          hasRedemptions: Boolean(detail.has_redemptions),
          currentPrice: detail.current_price ?? detail.price_per_vehicle ?? null,
          pendingPrice: detail.pending_terms?.price_per_vehicle ?? null,
          pendingEffectiveFrom: detail.pending_terms?.effective_from ?? null,
          package: selectedPackage?.package_name || listRow?.package || '',
          packageId: packageId || selectedPackage?.id || '',
          location: listRow?.branch || '',
          locationIds,
          billingCycleType,
          startDate,
          expiryMonths: this.parseExpiryMonths(durationLabel),
          paymentTermDays: detail.payment_term_days ?? listRow?.paymentTermDays ?? 30,
          vehicleCount: detail.vehicle_count_declared ?? listRow?.vehicleCount ?? 0,
          avgWashesPerMonth: detail.avg_washes_month ?? null,
          notificationRecipients: recipients.join(', '),
        };

        this.editFleetData.set(formData);
        this.showEditFleetModal.set(true);
      },
      error: error => {
        console.error('Error loading fleet for edit:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Unable to Edit',
          detail: error.error?.message || error.message || 'Failed to load fleet details.',
          life: 4000,
        });
      },
    });
  }

  onCloseEditFleetModal(): void {
    this.showEditFleetModal.set(false);
    this.editFleetData.set(null);
  }

  /**
   * Save edited fleet: validates input, resolves package ID, sends update request, and manages UI state, notifications, and data reload.
   * @example
   * nSaveEditFleet({ fleetId: 'fleet-123', package: 'Basic', /* other fields */
  onSaveEditFleet(formData: AddFleetFormData): void {
    if (formData.originalStatus && formData.status !== formData.originalStatus) {
      this.pendingStatusChange.set(formData);
      return;
    }
    this.saveEditFleet(formData);
  }

  onConfirmStatusChange(): void {
    const pending = this.pendingStatusChange();
    this.pendingStatusChange.set(null);
    if (pending) {
      this.saveEditFleet(pending);
    }
  }

  onCancelStatusChange(): void {
    this.pendingStatusChange.set(null);
  }

  private saveEditFleet(formData: AddFleetFormData): void {
    // Validate fleet ID exists
    if (!formData.fleetId) {
      console.error('Fleet ID is missing');
      this.messageService.add({
        severity: 'error',
        summary: 'Update Failed',
        detail: 'Fleet ID is missing.',
        life: 3000,
      });
      return;
    }

    // Get package ID either from formData or find it
    let packageId = formData.packageId;
    if (!packageId) {
      const selectedPackage = this.packages().find(
        (pkg: Package) => pkg.package_name === formData.package
      );
      if (!selectedPackage) {
        console.error('Selected package not found');
        this.messageService.add({
          severity: 'error',
          summary: 'Update Failed',
          detail: 'Selected package not found.',
          life: 3000,
        });
        return;
      }
      const { id } = selectedPackage;
      packageId = id;
    }

    // Start transition process
    this.isTransitioning.set(true);
    const fleetData = this.buildFleetPayload(formData, packageId);

    this.fleetService.updateFleet(formData.fleetId, fleetData).subscribe({
      next: response => {
        // Close modal immediately
        this.showEditFleetModal.set(false);
        this.editFleetData.set(null);

        // Show success message; say when a deferred price/package change starts.
        const schedule = response?.data?.terms_schedule;
        this.messageService.add({
          severity: 'success',
          summary: 'Fleet Updated',
          detail:
            schedule && !schedule.applied_immediately
              ? `Fleet has been updated. New price takes effect on ${this.formatCalendarDate(
                  schedule.effective_from
                )}.`
              : 'Fleet has been updated successfully.',
          life: 3000,
        });

        // Add a small delay before showing the table with updated data
        setTimeout(() => {
          this.isTransitioning.set(false);

          // Reload fleet data and statistics
          this.loadFleetData();

          // Reload fleet statistics to update stats
          this.loadFleetStatistics();
        }, 300);
      },
      error: error => {
        console.error('Error updating fleet:', error);
        this.isTransitioning.set(false);

        // Show error message
        this.messageService.add({
          severity: 'error',
          summary: 'Update Failed',
          detail: error.error?.message || 'Failed to update fleet. Please try again.',
          life: 3000,
        });
      },
    });
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
